'use client';

import * as React from 'react';
import { tv } from 'tailwind-variants';

import { useHorizontalScrollIndicators } from '../hooks/useHorizontalScrollIndicators';

import type { VariantProps } from 'tailwind-variants';

const table = tv({
  slots: {
    wrapper: `
      relative
      min-h-0
      w-full
      flex-1
    `,
    root: `
      h-full
      w-full
      overflow-auto
    `,
    leftIndicator: `
      pointer-events-none
      absolute
      top-0
      left-0
      z-0
      w-8
      bg-gradient-to-r
      from-background
      to-transparent
      transition-opacity
      duration-200
    `,
    rightIndicator: `
      pointer-events-none
      absolute
      top-0
      z-0
      w-8
      bg-gradient-to-l
      from-background
      to-transparent
      transition-opacity
      duration-200
    `,
    base: `
      w-full
      caption-bottom
      text-sm
    `,
    header: `
      [&_tr]:bg-background
      sticky
      top-0
      z-1
      [&_tr]:border-b-0
      [&_tr]:shadow-[0_1px_0_0_var(--border-secondary)]
    `,
    body: `
      [&_tr:last-child]:border-0
    `,
    footer: `
      bg-muted/50
      border-t
      font-medium
      [&>tr]:last:border-b-0
    `,
    // `data-active` is its own attribute because `data-state` holds a single value and is already
    // spoken for by `selected`: a row can be both selected and the one being viewed. The inset bar
    // is carried by a property no other row state sets, so it survives the selected and hover fills.
    row: `
      data-[state=selected]:bg-muted
      border-b
      transition-colors
      data-[active=true]:bg-background-accent-light
      data-[active=true]:shadow-[inset_3px_0_0_0_var(--background-accent)]
    `,
    head: `
      text-foreground
      px-2
      py-4
      text-left
      align-middle
      font-medium
      whitespace-nowrap
      [&:has([role=checkbox])]:pr-0
      [&>[role=checkbox]]:translate-y-[2px]
      `,
    cell: `
      px-2
      py-4
      align-middle
      whitespace-nowrap
      [&:has([role=checkbox])]:pr-0
      [&>[role=checkbox]]:translate-y-[2px]
    `,
    caption: `
      text-muted-foreground
      mt-4
      text-sm
    `,
  },
  variants: {
    rowHoverable: {
      true: {
        row: 'hover:bg-background-secondary cursor-pointer',
      },
    },
    inset: {
      false: {
        cell: 'first:pl-0 last:pr-0',
      },
    },
    // Darkening edge shadow instead of the default fade-to-background: a black scrim that reads as a
    // shadow over cell content and whitespace alike (unlike the fade, which vanishes over short/empty
    // edge cells). Lighter in light mode where the same opacity reads heavier on white.
    scrollShadow: {
      true: {
        leftIndicator: 'from-black/10 dark:from-black/15',
        rightIndicator: 'from-black/10 dark:from-black/15',
      },
    },
  },
  defaultVariants: {
    inset: true,
  },
});

const {
  wrapper: tableWrapper,
  root: tableRoot,
  leftIndicator: tableLeftIndicator,
  rightIndicator: tableRightIndicator,
  base: tableBase,
  header: tableHeader,
  body: tableBody,
  footer: tableFooter,
  row: tableRow,
  head: tableHead,
  cell: tableCell,
  caption: tableCaption,
} = table();

type TableProps = React.ComponentProps<'table'> & VariantProps<typeof table>;

function Table({ className, scrollShadow, ...props }: TableProps): React.ReactElement {
  const { ref, canScrollLeft, canScrollRight, isScrollable, scrollbarGutter } =
    useHorizontalScrollIndicators<HTMLDivElement>();

  return (
    <div className={tableWrapper()}>
      <div className={tableRoot()} data-slot="table-container" ref={ref} tabIndex={isScrollable ? 0 : undefined}>
        <table className={tableBase({ className })} data-slot="table" {...props} />
      </div>
      <div
        className={tableLeftIndicator({ scrollShadow, className: canScrollLeft ? 'opacity-100' : 'opacity-0' })}
        aria-hidden
        style={{ bottom: scrollbarGutter.bottom }}
      />
      <div
        className={tableRightIndicator({ scrollShadow, className: canScrollRight ? 'opacity-100' : 'opacity-0' })}
        aria-hidden
        style={{ bottom: scrollbarGutter.bottom, right: scrollbarGutter.right }}
      />
    </div>
  );
}

type TableHeaderProps = React.ComponentProps<'thead'>;

function TableHeader({ className, ...props }: TableHeaderProps): React.ReactElement {
  return <thead className={tableHeader({ className })} data-slot="table-header" {...props} />;
}

type TableBodyProps = React.ComponentProps<'tbody'>;

function TableBody({ className, ...props }: TableBodyProps): React.ReactElement {
  return <tbody className={tableBody({ className })} data-slot="table-body" {...props} />;
}

type TableFooterProps = React.ComponentProps<'tfoot'>;

function TableFooter({ className, ...props }: TableFooterProps): React.ReactElement {
  return <tfoot className={tableFooter({ className })} data-slot="table-footer" {...props} />;
}

type TableRowProps = React.ComponentProps<'tr'> & VariantProps<typeof table>;

function TableRow({ className, ...props }: TableRowProps): React.ReactElement {
  const { rowHoverable, ...rest } = props;
  return <tr className={tableRow({ className, rowHoverable })} data-slot="table-row" {...rest} />;
}

type TableHeadProps = React.ComponentProps<'th'>;

function TableHead({ className, ...props }: TableHeadProps): React.ReactElement {
  return <th className={tableHead({ className })} data-slot="table-head" {...props} />;
}

type TableCellProps = React.ComponentProps<'td'> & VariantProps<typeof table>;

function TableCell({ className, ...props }: TableCellProps): React.ReactElement {
  const { inset, ...rest } = props;
  return <td className={tableCell({ className, inset })} data-slot="table-cell" {...rest} />;
}

type TableCaptionProps = React.ComponentProps<'caption'>;

function TableCaption({ className, ...props }: TableCaptionProps): React.ReactElement {
  return <caption className={tableCaption({ className })} data-slot="table-caption" {...props} />;
}

export {
  table,
  Table,
  TableHeader,
  TableBody,
  TableFooter,
  TableHead,
  TableRow,
  TableCell,
  TableCaption,
  type TableProps,
  type TableHeaderProps,
  type TableBodyProps,
  type TableFooterProps,
  type TableHeadProps,
  type TableRowProps,
  type TableCellProps,
  type TableCaptionProps,
};
